import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Separator,
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  useSidebar,
} from '@repo/ui'
import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
  useNavigate,
} from '@tanstack/react-router'
import {
  BadgeCheckIcon,
  BellIcon,
  BookOpenIcon,
  Building2Icon,
  CalendarIcon,
  ChevronsUpDownIcon,
  CircleIcon,
  InboxIcon,
  LayoutDashboardIcon,
  LogOutIcon,
  MailIcon,
  MegaphoneIcon,
  PlugZapIcon,
  PlusIcon,
  RepeatIcon,
  ShieldCheckIcon,
  SmartphoneIcon,
  SmilePlusIcon,
  SparklesIcon,
  Users2Icon,
  UsersIcon,
} from 'lucide-react'
import { type UIEvent, useEffect, useMemo } from 'react'

import agroIcon from '@/assets/agrosdr-icon.png'
import { ChannelAlertBanner } from '@/components/common/channel-alert-banner'
import { PromoPopupHost } from '@/components/common/promo-popup-host'
import { SupportButton } from '@/components/common/support-button'
import { useAuth } from '@/hooks/use-auth'
import { useChannels } from '@/hooks/use-channels'
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  type UserNotification,
} from '@/hooks/use-notifications'

export const Route = createFileRoute('/_app')({
  component: AppLayout,
})

const NAV_GROUPS = [
  {
    label: 'Atendimento',
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboardIcon },
      { to: '/inbox', label: 'Inbox', icon: InboxIcon },
      { to: '/leads', label: 'Leads', icon: UsersIcon },
      { to: '/agendamentos', label: 'Agendamentos', icon: CalendarIcon },
      { to: '/nps', label: 'NPS', icon: SmilePlusIcon },
    ],
  },
  {
    label: 'IA / Automações',
    items: [
      { to: '/agentes', label: 'Agentes IA', icon: SparklesIcon },
      {
        to: '/conhecimento',
        label: 'Base de conhecimento',
        icon: BookOpenIcon,
      },
      { to: '/cadencias', label: 'Cadências', icon: RepeatIcon },
      {
        to: '/campanhas',
        label: 'Campanhas',
        icon: MegaphoneIcon,
        adminOnly: true,
      },
      { to: '/grupos', label: 'Grupos', icon: Users2Icon, adminOnly: true },
    ],
  },
  {
    label: 'Sistema',
    items: [
      { to: '/canais', label: 'Canais', icon: SmartphoneIcon },
      { to: '/integracoes', label: 'Integrações', icon: PlugZapIcon },
      { to: '/organizacao', label: 'Organização', icon: Building2Icon },
    ],
  },
]

const BREADCRUMB_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  relatorio: 'Relatório',
  agentes: 'Agentes IA',
  agendamentos: 'Agendamentos',
  ajuda: 'Ajuda',
  campanhas: 'Campanhas',
  nps: 'NPS',
  templates: 'Templates',
  cadencias: 'Cadências',
  grupos: 'Grupos',
  conhecimento: 'Conhecimento',
  inbox: 'Inbox',
  leads: 'Leads',
  midias: 'Mídias',
  duplicados: 'Duplicados',
  kanban: 'Kanban',
  config: 'Configurações',
  integracoes: 'Integrações',
  organizacao: 'Organização',
  assinatura: 'Assinatura',
  membros: 'Membros',
  setores: 'Setores',
  tags: 'Tags',
  campos: 'Campos',
  'motivos-finalizacao': 'Motivos de finalização',
  'respostas-rapidas': 'Respostas rápidas',
  whatsapp: 'WhatsApp',
  mensagens: 'Mensagens',
  privacidade: 'Privacidade',
  canais: 'Canais',
  convites: 'Convites',
  perfil: 'Perfil',
  perfis: 'Perfis',
  novo: 'Novo',
  // Agente
  qualificacao: 'Qualificação',
  agendamento: 'Agendamento',
  automacoes: 'Automações',
  seguranca: 'Segurança',
  mcp: 'MCP',
  configuracoes: 'Configurações',
  otimizador: 'Otimizador',
  testes: 'Testes',
  // Cadência
  etapas: 'Etapas',
  agenda: 'Janela e regras',
  // Integrações
  'rd-marketing': 'RD Station Marketing',
  'exact-spotter': 'Exact Spotter',
  'google-calendar': 'Google Calendar',
  webhooks: 'Webhooks',
  'webhooks-saida': 'Webhooks de saída',
  logs: 'Logs',
  callback: 'Conexão',
}

function getInitials(value?: string | null) {
  if (!value) return 'OR'
  return value
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
}

// Todo segmento fixo das rotas tem rótulo acima; o que sobra é id (cuid) e
// vira "Detalhe", como no admin. O nome do registro já está no PageHeader.
function toBreadcrumbLabel(segment: string) {
  return BREADCRUMB_LABELS[segment] ?? 'Detalhe'
}

// --- OrgSwitcher ---
function OrgSwitcher() {
  const { memberships, currentOrgId, switchOrg } = useAuth()
  const { isMobile } = useSidebar()
  const navigate = useNavigate()
  const currentOrg = memberships.find(
    (m) => m.organizationId === currentOrgId
  )?.organization

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="size-8 rounded-lg">
                {currentOrg?.logo ? (
                  <AvatarImage src={currentOrg.logo} alt="" />
                ) : null}
                <AvatarFallback className="bg-sidebar-primary text-sidebar-primary-foreground rounded-lg text-xs font-semibold">
                  {getInitials(currentOrg?.name)}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">
                  {currentOrg?.name ?? '—'}
                </span>
              </div>
              <ChevronsUpDownIcon className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            align="start"
            side={isMobile ? 'bottom' : 'right'}
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-muted-foreground text-xs">
              Organizações
            </DropdownMenuLabel>
            {memberships.map((m) => (
              <DropdownMenuItem
                key={m.organizationId}
                onClick={() => switchOrg(m.organizationId)}
                className="gap-2 p-2"
              >
                <Avatar className="size-6 rounded-md">
                  {m.organization.logo ? (
                    <AvatarImage src={m.organization.logo} alt="" />
                  ) : null}
                  <AvatarFallback className="text-2xs rounded-md border bg-transparent font-semibold">
                    {getInitials(m.organization.name)}
                  </AvatarFallback>
                </Avatar>
                {m.organization.name}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="gap-2 p-2"
              onClick={() => {
                void navigate({
                  to: '/onboarding',
                  search: { new: true, step: 1 },
                })
              }}
            >
              <div className="flex size-6 items-center justify-center rounded-md border bg-transparent">
                <PlusIcon className="size-4" />
              </div>
              <span className="text-muted-foreground font-medium">
                Nova organização
              </span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}

// --- WhatsApp status ---
function WhatsAppStatus({ orgId }: { orgId: string }) {
  const { data: channels = [] } = useChannels(orgId)
  const { isMobile } = useSidebar()

  if (channels.length === 0) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          tooltip="Conectar número"
          className="text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        >
          <Link to="/integracoes">
            <PlusIcon className="size-4" />
            <span className="text-xs">Conectar número</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    )
  }

  const connectedCount = channels.filter((c) => c.status === 'CONNECTED').length
  const first = channels[0]

  return (
    <SidebarMenuItem>
      <Tooltip>
        <TooltipTrigger asChild>
          <SidebarMenuButton className="cursor-default">
            <SmartphoneIcon className="size-4" />
            <span className="truncate text-xs">
              {first?.name ?? 'WhatsApp'}
            </span>
          </SidebarMenuButton>
        </TooltipTrigger>
        <TooltipContent side={isMobile ? 'top' : 'right'} className="p-3">
          <p className="mb-2 text-xs font-medium">Números WhatsApp</p>
          <div className="flex flex-col gap-1.5">
            {channels.map((ch) => (
              <div key={ch.id} className="flex items-center gap-2 text-xs">
                <CircleIcon
                  className={cn(
                    'size-2 fill-current',
                    ch.status === 'CONNECTED'
                      ? 'text-success'
                      : 'text-destructive'
                  )}
                />
                <span>{ch.name}</span>
                <span className="text-muted-foreground ml-auto">
                  {ch.status === 'CONNECTED' ? 'Online' : 'Offline'}
                </span>
              </div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
      <SidebarMenuBadge>
        <span className="text-2xs flex items-center gap-1">
          <CircleIcon
            className={cn(
              'size-1.5 fill-current',
              connectedCount > 0 ? 'text-success' : 'text-destructive'
            )}
          />
          {connectedCount}/{channels.length}
        </span>
      </SidebarMenuBadge>
    </SidebarMenuItem>
  )
}

// --- AppSidebar ---
function AppSidebar({ orgId, pathname }: { orgId: string; pathname: string }) {
  const { currentRole } = useAuth()
  const isAdmin = currentRole === 'OWNER' || currentRole === 'ADMIN'
  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <div className="border-sidebar-border flex h-16 items-center gap-2 border-b px-2 group-data-[collapsible=icon]:justify-center">
          <img
            src={agroIcon}
            alt="AgroSDR"
            className="size-12 shrink-0 object-contain group-data-[collapsible=icon]:size-8"
          />
          <span className="text-sidebar-foreground truncate font-semibold tracking-tight group-data-[collapsible=icon]:hidden">
            AgroSDR
          </span>
        </div>
        <OrgSwitcher />
      </SidebarHeader>

      <SidebarContent>
        {NAV_GROUPS.filter(
          (group) => group.label !== 'Em desenvolvimento' || import.meta.env.DEV
        ).map((group) => (
          <SidebarGroup key={group.label}>
            <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items
                  .filter((item) => {
                    const adminOnly =
                      'adminOnly' in item && item.adminOnly === true
                    return !adminOnly || isAdmin
                  })
                  .map((item) => {
                    const isActive =
                      item.to === '/inbox'
                        ? pathname === '/inbox' ||
                          pathname.startsWith('/inbox/')
                        : pathname.startsWith(item.to)
                    return (
                      <SidebarMenuItem key={item.to}>
                        <SidebarMenuButton
                          asChild
                          isActive={isActive}
                          tooltip={item.label}
                          className={
                            isActive
                              ? undefined
                              : 'text-sidebar-foreground/70 hover:text-sidebar-foreground'
                          }
                        >
                          <Link to={item.to}>
                            <item.icon className="size-4" />
                            <span>{item.label}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )
                  })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}

        <SidebarGroup className="mt-auto">
          <SidebarGroupLabel>WhatsApp</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <WhatsAppStatus orgId={orgId} />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  )
}

// --- Topbar: notifications ---
function TopbarNotifications() {
  const navigate = useNavigate()
  const { currentOrgId } = useAuth()
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useNotifications(currentOrgId)
  const markRead = useMarkNotificationRead(currentOrgId)
  const markAllRead = useMarkAllNotificationsRead(currentOrgId)
  const notifications = data?.pages.flatMap((page) => page.data) ?? []
  const unreadCount = data?.pages[0]?.unreadCount ?? 0
  const unreadBadgeLabel = unreadCount > 99 ? '99+' : String(unreadCount)
  const unreadSummary =
    unreadCount === 0
      ? 'Tudo lido'
      : unreadCount === 1
        ? '1 não lida'
        : unreadCount > 99
          ? '99+ não lidas'
          : `${unreadCount} não lidas`

  async function openNotification(notification: UserNotification) {
    if (!notification.readAt) {
      await markRead.mutateAsync(notification.id)
    }

    if (!notification.actionUrl) return

    const url = new URL(notification.actionUrl, window.location.origin)
    const inboxId =
      url.pathname === '/inbox' || url.pathname === '/atendimentos'
        ? url.searchParams.get('id')
        : null
    if (inboxId) {
      void navigate({
        to: '/inbox',
        search: {
          id: inboxId,
        },
      })
      return
    }

    window.location.assign(notification.actionUrl)
  }

  function handleNotificationsScroll(event: UIEvent<HTMLDivElement>) {
    const target = event.currentTarget
    const distanceToBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight
    if (distanceToBottom > 48 || !hasNextPage || isFetchingNextPage) return
    void fetchNextPage()
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-8 w-8"
          aria-label="Abrir notificações"
        >
          <BellIcon className="h-4 w-4" />
          {unreadCount > 0 ? (
            <span
              className={cn(
                'bg-destructive text-destructive-foreground text-2xs absolute top-0 right-0 flex h-4 translate-x-1/3 -translate-y-1/3 items-center justify-center rounded-full leading-none font-semibold tabular-nums',
                unreadCount > 99
                  ? 'min-w-6 px-1'
                  : unreadCount > 9
                    ? 'min-w-5 px-1'
                    : 'w-4 px-0'
              )}
            >
              {unreadBadgeLabel}
            </span>
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="w-72">
        <div className="flex items-center justify-between gap-2 px-2 py-1.5">
          <DropdownMenuLabel className="p-0">
            <span className="block">Notificações</span>
            <span className="text-muted-foreground block text-xs font-normal">
              {unreadSummary}
            </span>
          </DropdownMenuLabel>
          {unreadCount > 0 ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => void markAllRead.mutateAsync()}
            >
              Ler todas
            </Button>
          ) : null}
        </div>
        <DropdownMenuSeparator />
        {notifications.length === 0 ? (
          <p className="text-muted-foreground px-2 py-6 text-center text-sm">
            Nenhuma notificação
          </p>
        ) : (
          <div
            className="max-h-96 overflow-y-auto"
            onScroll={handleNotificationsScroll}
          >
            {notifications.map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                className="flex cursor-pointer items-start gap-2 px-2 py-2"
                onClick={() => void openNotification(notification)}
              >
                <span
                  className={cn(
                    'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                    notification.readAt ? 'bg-muted' : 'bg-primary'
                  )}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">
                    {notification.title}
                  </span>
                  <span className="text-muted-foreground line-clamp-2 block text-xs">
                    {notification.body}
                  </span>
                </span>
              </DropdownMenuItem>
            ))}
            {isFetchingNextPage ? (
              <div className="text-muted-foreground flex items-center justify-center gap-2 px-2 py-3 text-xs">
                <Spinner className="h-3.5 w-3.5" />
                Carregando
              </div>
            ) : null}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// --- Topbar: user menu ---
function TopbarUser() {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const initials = getInitials(user?.name ?? user?.email)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-8 w-8 rounded-full p-0"
          aria-label="Abrir menu do usuário"
        >
          <Avatar className="h-8 w-8 rounded-full">
            {user?.image ? <AvatarImage src={user.image} alt="" /> : null}
            <AvatarFallback className="bg-primary/10 text-primary rounded-full text-xs font-semibold">
              {initials}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="w-56">
        <DropdownMenuLabel className="p-0 font-normal">
          <div className="flex items-center gap-2 px-2 py-2">
            <Avatar className="h-8 w-8 rounded-lg">
              {user?.image ? <AvatarImage src={user.image} alt="" /> : null}
              <AvatarFallback className="bg-primary/10 text-primary rounded-lg text-xs font-semibold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium">
                {user?.name ?? 'Usuário'}
              </span>
              <span className="text-muted-foreground truncate text-xs">
                {user?.email ?? ''}
              </span>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() =>
            void navigate({ to: '/config', search: { tab: 'perfil' } })
          }
          className="gap-2"
        >
          <BadgeCheckIcon className="size-4" />
          Perfil
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() =>
            void navigate({ to: '/config', search: { tab: 'seguranca' } })
          }
          className="gap-2"
        >
          <ShieldCheckIcon className="size-4" />
          Segurança
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() =>
            void navigate({ to: '/config', search: { tab: 'calendario' } })
          }
          className="gap-2"
        >
          <CalendarIcon className="size-4" />
          Calendário
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            void navigate({ to: '/convites' })
          }}
          className="gap-2"
        >
          <MailIcon className="size-4" />
          Meus convites
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            void navigate({ to: '/organizacao' })
          }}
          className="gap-2"
        >
          <Building2Icon className="size-4" />
          Organização
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={async () => {
            await signOut()
            window.location.href = '/entrar'
          }}
          className="text-destructive focus:text-destructive gap-2"
        >
          <LogOutIcon className="size-4" />
          Sair
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// --- Topbar ---
function AppTopbar({ pathname }: { pathname: string }) {
  const breadcrumbs = useMemo(() => {
    const parts = pathname.split('/').filter(Boolean)
    return parts.map((part, index) => ({
      href: `/${parts.slice(0, index + 1).join('/')}`,
      label: toBreadcrumbLabel(part),
      current: index === parts.length - 1,
    }))
  }, [pathname])

  return (
    <header className="border-border bg-card flex h-14 shrink-0 items-center gap-3 border-b px-4">
      <SidebarTrigger />
      <Separator orientation="vertical" className="h-5" />
      <Breadcrumb className="hidden md:block">
        <BreadcrumbList>
          {breadcrumbs.map((item, index) => (
            <div key={item.href} className="inline-flex items-center gap-1.5">
              <BreadcrumbItem>
                {item.current ? (
                  <BreadcrumbPage className="max-w-44 truncate text-xs">
                    {item.label}
                  </BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild className="max-w-36 truncate text-xs">
                    <Link to={item.href}>{item.label}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {index < breadcrumbs.length - 1 ? <BreadcrumbSeparator /> : null}
            </div>
          ))}
        </BreadcrumbList>
      </Breadcrumb>
      <div className="flex-1" />
      <div className="flex items-center gap-3">
        <SupportButton />
        <TopbarNotifications />
        <TopbarUser />
      </div>
    </header>
  )
}

// --- Root layout ---
function AppLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, memberships, currentOrgId, loading } = useAuth()
  const pathname = location.pathname
  const returnTo =
    pathname === '/entrar'
      ? undefined
      : `${location.pathname}${location.searchStr}`

  useEffect(() => {
    if (loading) return
    if (!user) {
      void navigate({ to: '/entrar', search: { returnTo } })
      return
    }
    const mustChangePassword =
      user.mustChangePassword === true || user.must_change_password === true
    if (mustChangePassword && pathname !== '/alterar-senha') {
      void navigate({ to: '/alterar-senha' })
      return
    }
    if (memberships.length === 0 || !currentOrgId) {
      void navigate({ to: '/sem-organizacao' })
    }
  }, [
    currentOrgId,
    loading,
    memberships.length,
    navigate,
    pathname,
    returnTo,
    user,
  ])

  if (loading) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center">
        <Spinner className="text-muted-foreground h-6 w-6" />
      </div>
    )
  }

  if (!user || memberships.length === 0 || !currentOrgId) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center">
        <Spinner className="text-muted-foreground h-6 w-6" />
      </div>
    )
  }

  return (
    <TooltipProvider delayDuration={150}>
      <SidebarProvider>
        <AppSidebar orgId={currentOrgId} pathname={pathname} />
        <SidebarInset className="min-w-0">
          <AppTopbar pathname={pathname} />
          <ChannelAlertBanner orgId={currentOrgId} />
          <PromoPopupHost orgId={currentOrgId} />
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <Outlet />
          </div>
        </SidebarInset>
      </SidebarProvider>
    </TooltipProvider>
  )
}
